import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertTriangle, X } from 'lucide-react';
import clsx from 'clsx';
import {
  cleanCustomerDetails,
  customerDetailsProblems,
  isAuPhone,
  phoneKey,
  type CustomerDetailField,
  type CustomerDetails,
} from '@atink/domain';
import { useBrainStore } from '@/state/brainStore';
import { useUIStore } from '@/state/uiStore';
import { customerDetailsOf, customerWithMobile, saveCustomerDetails } from '@/domain/customerDetailsFlow';
import { customerName } from '@/utils/format';
import type { CustomerRecord } from '@/domain/types';

interface Props {
  open: boolean;
  onClose: () => void;
  customer: CustomerRecord;
}

const INPUT =
  'mt-2 w-full h-11 bg-ink-black border rounded-md px-3 text-sm text-cream focus:outline-none focus:border-gold-bright';

/**
 * "Edit details" on the customer record (tv-pzvy, approved mockup 6 Oct): first,
 * last and preferred name, mobile, email, Instagram handle and the address.
 * Inline checks use the server's own rules (`@atink/domain` customerDetails). A
 * mobile already on another customer warns with a link to that record, and the
 * save still goes ahead. Saving is one server intent; the pop-up waits for its
 * answer, then closes with "Details saved", or shows the server's reason.
 */
export default function EditCustomerDetailsModal({ open, onClose, customer }: Props) {
  return (
    <AnimatePresence>
      {open ? <EditCustomerDetailsDialog key={customer.id} customer={customer} onClose={onClose} /> : null}
    </AnimatePresence>
  );
}

function EditCustomerDetailsDialog({ customer, onClose }: { customer: CustomerRecord; onClose: () => void }) {
  const onFile = useMemo(() => customerDetailsOf(customer), [customer.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const [form, setForm] = useState<CustomerDetails>(onFile);
  const [touched, setTouched] = useState<Partial<Record<CustomerDetailField, boolean>>>({});
  const [saving, setSaving] = useState(false);
  const [serverProblem, setServerProblem] = useState<{ field?: CustomerDetailField; message: string } | null>(null);
  const customers = useBrainStore((s) => s.customers);

  const problems = useMemo(() => customerDetailsProblems(cleanCustomerDetails(form), onFile), [form, onFile]);
  // A field shows its problem once edited; one already wrong on file shows it at
  // once, so a disabled Save always says why.
  const problemFor = (f: CustomerDetailField): string | undefined =>
    touched[f] || form[f] === onFile[f] ? problems.find((p) => p.field === f)?.message : undefined;
  const mobileChanged = phoneKey(form.phone) !== phoneKey(onFile.phone);
  const duplicate = useMemo(
    () => (mobileChanged && isAuPhone(form.phone) ? customerWithMobile(customers, form.phone, customer.id) : undefined),
    [mobileChanged, form.phone, customers, customer.id],
  );
  const firstName = customer.preferredName || customer.firstName;
  const dialogRef = useRef<HTMLDivElement>(null);
  const savingRef = useRef(false);
  const close = () => {
    if (!savingRef.current) onClose();
  };

  // Focus the first field on open, give focus back to the opener on close, keep
  // Tab inside the pop-up, and close on Escape (never mid-save).
  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    dialogRef.current?.querySelector<HTMLInputElement>('input')?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        if (!savingRef.current) onClose();
        return;
      }
      if (e.key !== 'Tab' || !dialogRef.current) return;
      const focusable = Array.from(
        dialogRef.current.querySelectorAll<HTMLElement>('input, button:not([disabled]), a[href]'),
      );
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (!first || !last) return;
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      opener?.focus?.();
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (f: CustomerDetailField) => (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [f]: value }));
    setTouched((prev) => ({ ...prev, [f]: true }));
    setServerProblem(null);
  };

  const save = async () => {
    if (problems.length || savingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    const res = await saveCustomerDetails(customer.id, form, onFile);
    savingRef.current = false;
    setSaving(false);
    if (res.ok) {
      if (!res.unchanged) {
        useUIStore.getState().pushNotification({
          intensity: 'info',
          title: 'Details saved',
          ttlMs: 5000,
          customerId: customer.id,
        });
      }
      onClose();
      return;
    }
    if ('problems' in res) {
      setTouched(Object.fromEntries(res.problems.map((p) => [p.field, true])));
      return;
    }
    setServerProblem(res.refused);
  };

  const field = (
    f: CustomerDetailField,
    label: string,
    opts: { type?: string; placeholder?: string; mono?: boolean; describedBy?: string } = {},
  ) => {
    const problem = problemFor(f) ?? (serverProblem?.field === f ? serverProblem.message : undefined);
    const errorId = `edit-details-${f}-error`;
    return (
      <label className="block">
        <span className="label-small-caps">{label}</span>
        <input
          type={opts.type ?? 'text'}
          value={form[f]}
          onChange={set(f)}
          placeholder={opts.placeholder}
          aria-invalid={problem ? true : undefined}
          aria-describedby={[problem ? errorId : '', opts.describedBy ?? ''].filter(Boolean).join(' ') || undefined}
          className={clsx(INPUT, opts.mono && 'font-mono', problem ? 'border-blood' : 'border-gold-dim/40')}
          data-testid={`edit-details-${TESTID[f]}`}
        />
        {problem ? (
          <span id={errorId} className="mt-1 block font-inter text-xs text-blood" data-testid={`edit-details-${TESTID[f]}-error`}>
            {problem}
          </span>
        ) : null}
      </label>
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-overlay/80 backdrop-blur-sm flex items-start justify-center overflow-y-auto px-4 py-10"
      onClick={close}
    >
      <motion.div
        initial={{ y: 8, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 8, opacity: 0 }}
        transition={{ duration: 0.2 }}
        className="bg-ink-deep border border-gold-dim/40 rounded-xl w-full max-w-2xl p-6 sm:p-8 relative flex flex-col gap-5"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-details-title"
        data-testid="edit-customer-details-modal"
      >
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 id="edit-details-title" className="heading-display text-2xl text-cream">
              Edit customer details
            </h2>
            <p className="font-serif text-sm text-cream-soft mt-1">
              Changes save to {firstName}&rsquo;s record and show in their history.
            </p>
          </div>
          <button
            type="button"
            onClick={close}
            disabled={saving}
            className="h-11 w-11 shrink-0 inline-flex items-center justify-center rounded-full text-cream-soft hover:text-cream"
            aria-label="Close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {field('firstName', 'First name')}
          {field('lastName', 'Last name')}
          {field('preferredName', 'Preferred name · optional', { placeholder: 'What texts call them' })}
          {/* Required once on file: it can be changed, not cleared. A DM lead with
              no number yet can still be saved without one. */}
          {field('phone', onFile.phone ? 'Mobile · required' : 'Mobile', {
            type: 'tel',
            mono: true,
            ...(duplicate ? { describedBy: 'edit-details-duplicate' } : {}),
          })}
          {duplicate ? (
            <div
              id="edit-details-duplicate"
              role="status"
              className="sm:col-span-2 flex gap-3 items-start rounded-lg border border-orange/45 bg-orange/10 px-4 py-3"
              data-testid="edit-details-duplicate"
            >
              <AlertTriangle size={18} className="text-orange shrink-0 mt-0.5" aria-hidden="true" />
              <div className="flex flex-col gap-1 font-inter text-[13px] text-cream">
                <span>
                  <strong>This mobile is already on another customer:</strong> {customerName(duplicate)}
                  {duplicate.suburb ? `, ${duplicate.suburb}` : ''}.
                </span>
                <span>
                  <Link to={`/customer/${duplicate.id}`} className="text-gold-bright underline" onClick={onClose}>
                    Open that record
                  </Link>{' '}
                  to check whether they&rsquo;re the same person. You can still save.
                </span>
              </div>
            </div>
          ) : null}
          {field('email', 'Email · optional', { type: 'email' })}
          {field('instagramHandle', 'Instagram handle · optional', { describedBy: 'edit-details-ig-note' })}
          <p id="edit-details-ig-note" className="sm:col-span-2 -mt-2 font-inter text-xs text-cream-soft">
            Changing the handle doesn&rsquo;t move their Instagram messages. Those stay linked to the account they messaged
            from.
          </p>
          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-[2fr_1fr_1fr] gap-4">
            {field('suburb', 'Suburb')}
            {field('state', 'State')}
            {field('postcode', 'Postcode', { mono: true })}
          </div>
        </div>

        {serverProblem && !serverProblem.field ? (
          <p className="font-inter text-xs text-blood" role="alert" data-testid="edit-details-server-error">
            {serverProblem.message}
          </p>
        ) : null}

        <div className="flex justify-end gap-3 border-t border-gold-dim/30 pt-4">
          <button type="button" onClick={close} disabled={saving} className="btn-ghost text-xs">
            Cancel
          </button>
          <button
            type="button"
            onClick={() => void save()}
            disabled={problems.length > 0 || saving}
            className="btn-primary text-xs disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="edit-details-save"
          >
            {saving ? 'Saving…' : 'Save details'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}

const TESTID: Record<CustomerDetailField, string> = {
  firstName: 'first-name',
  lastName: 'last-name',
  preferredName: 'preferred-name',
  phone: 'mobile',
  email: 'email',
  instagramHandle: 'instagram',
  suburb: 'suburb',
  state: 'state',
  postcode: 'postcode',
};
